/**
 * OPUS-913 — live presence avatar stack (who is viewing a renderer now).
 * Consumes the Renderer_Service presence API shape: viewers = [{ userId, name, since }]
 * where `since` is an epoch-ms join time. Matches the design's PresenceStack:
 * overlapping initials avatars + a green live count, click to expand the list.
 */
import React, { useState } from "react";
import { Icon } from "../common/opus";

const AVATAR_COLORS = ["#FF455C", "#45FFA7", "#58C4FF", "#C445FF", "#FCC144", "#5C6266", "#FF8A5B", "#2BD4A0"];

function colorFor(key) {
  const s = String(key || "?");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  return (String(name || "?").trim().split(/[\s_.@-]+/).slice(0, 2).map((w) => w[0]).join("") || "?").toUpperCase();
}
function relTime(since) {
  if (!since) return "";
  const t = typeof since === "number" ? since : Date.parse(since);
  if (!t || Number.isNaN(t)) return typeof since === "string" ? since : "";
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 45) return "now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
}

function PersonAvatar({ v, size = 28, ring = "var(--panel)" }) {
  // Honour the viewer's chosen avatar (preset icon or initials colour) from
  // Edit profile, carried through the presence heartbeat; fall back to a
  // hashed colour + initials when none is set.
  const a = v.avatar;
  const bg = (a && a.color) || colorFor(v.userId || v.name);
  return (
    <div title={v.name || v.userId} style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0, background: bg,
      color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, fontWeight: 700, boxShadow: `0 0 0 2px ${ring}`, fontFamily: "var(--font-sans)",
    }}>{a && a.type === "preset" && a.icon ? <Icon name={a.icon} size={Math.round(size * 0.5)} /> : initials(v.name || v.userId)}</div>
  );
}

export default function PresenceStack({ viewers, max = 4, size = 28, label = true }) {
  const [open, setOpen] = useState(false);
  if (!viewers || !viewers.length) return null;
  const shown = viewers.slice(0, max);
  const extra = viewers.length - shown.length;
  return (
    <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen((o) => !o)} title="Who's viewing now"
        style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "3px 9px 3px 4px", border: "1px solid var(--border)", borderRadius: 999, background: open ? "var(--surface-3)" : "var(--surface-1)", cursor: "pointer", transition: "background .13s" }}>
        <span style={{ display: "inline-flex" }}>
          {shown.map((v, i) => (
            <span key={v.userId || i} style={{ marginLeft: i ? -9 : 0, position: "relative", zIndex: shown.length - i }}>
              <PersonAvatar v={v} size={size} />
            </span>
          ))}
          {extra > 0 && (
            <span style={{ marginLeft: -9, width: size, height: size, borderRadius: "50%", background: "var(--surface-3)", color: "var(--text-muted)", fontSize: size * 0.36, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 0 2px var(--panel)" }}>+{extra}</span>
          )}
        </span>
        {label && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "var(--green)" }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: "var(--green)", boxShadow: "0 0 6px var(--green)" }} />
            {viewers.length}
          </span>
        )}
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 80 }} onClick={() => setOpen(false)} />
          <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 90, width: 268, background: "var(--elevated)", border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-lg)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: "1px solid var(--border)" }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: "var(--green)", boxShadow: "0 0 6px var(--green)" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Viewing now</span>
              <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-dim)", fontWeight: 600 }}>{viewers.length} {viewers.length === 1 ? "person" : "people"}</span>
            </div>
            <div style={{ maxHeight: 280, overflowY: "auto", padding: 6 }} className="opus-scroll">
              {viewers.map((v, i) => (
                <div key={v.userId || i} style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 9px", borderRadius: "var(--r-sm)" }}>
                  <PersonAvatar v={v} size={34} ring="var(--elevated)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.name || v.userId}</div>
                  </div>
                  <span style={{ fontSize: 11, color: "var(--text-dim)", whiteSpace: "nowrap" }}>{relTime(v.since)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
